import React, { useState, useEffect } from "react";
import { 
    Box, 
    Tabs, 
    Tab, 
    Badge, 
} from "@mui/material";
import {
    FlightTakeoff,
    Storefront,
    History,
    Sync,
} from "@mui/icons-material";
import useAuthCheck from "state/useAuthCheck";
import { LAYOUT_HEIGHT } from 'state/constants';
import api from "state/axios";
import db from "state/db";
// import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import SessionSetup from "./session/SessionSetup";
import SalesFloor from "./SalesFloor";
import OrderHistory from "./OrderHistory";
import SyncPanel from "./SyncPanel";
import { useSyncContext } from "contexts/SyncContext";
import useOnlineStatus from "./hooks/useOnlineStatus";


const InflightSales = () => {
    useAuthCheck();
    const [tab, setTab] = useState(0);
    const [session, setSession] = useState(null);
    const [loadingSession, setLoadingSession] = useState(true);

    const isOnline = useOnlineStatus();
    const { pendingCount } = useSyncContext();

    useEffect(() => {
        const loadActive = async () => {
            if (isOnline) {
                try {
                    const res = await api.get("/inflight/sessions/active");
                    if (res.data) {
                        // If we have a local pending_close for this session, don't
                        // override — user closed it offline and sync hasn't run yet
                        const local = await db.sessions.get(res.data._id);
                        if (local?.status === "closed") {
                            setSession(null);
                        } else {
                            await db.sessions.put({ ...res.data, syncStatus: "synced" });
                            setSession(res.data);
                        }
                    } else {
                        // Server has no active session — check Dexie for a pending_create one
                        const local = await db.sessions
                            .filter((s) => s.status === "active")
                            .first();
                        setSession(local || null);
                    }
                } catch {
                    setSession(null);
                }
            } else {
                const local = await db.sessions
                    .filter((s) => s.status === "active")
                    .first();
                setSession(local || null);
            }
            setLoadingSession(false);
        };
        loadActive();
    }, [isOnline]);

    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header 
                    title="Inflight Sales" 
                    subtitle={session
                        ? `Session active: ${session.flightDesc} ${session.dep}-${session.arr}`
                        : loadingSession
                            ? "Loading…"
                            : "No active session"
                    }
                /> 
            </FlexBetween>
            <Box
                display="grid"
                gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                gap={2}
            >
                <Box
                    height={LAYOUT_HEIGHT}  
                    sx={{ gridColumn: "span 12", overflowY: 'auto' }}
                >
                    {/* Tab bar */}
                    <Tabs                
                        variant="fullWidth"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        slotProps={{ indicator: { style: { height: 2 } } }}
                        sx={{
                            borderBottom: 1, 
                            borderColor: "divider",
                            bgcolor: "background.alt",
                            minHeight: 44,
                            '& .MuiTab-root': { minHeight: 44, py: 0.5 },
                        }}
                    >
                        <Tab
                            icon={<FlightTakeoff fontSize="small" />}
                            label="Session"
                            iconPosition="start"
                        />
                        <Tab
                            icon={<Storefront fontSize="small" />}
                            label="Sales"
                            iconPosition="start"
                            disabled={!session}
                        />
                        <Tab
                            icon={<History fontSize="small" />}
                            label="History"
                            iconPosition="start"
                        />
                        <Tab
                            icon={
                                <Badge badgeContent={pendingCount > 0 ? pendingCount : null} color="warning">
                                    <Sync fontSize="small" />
                                </Badge>
                            }
                            label="Sync"
                            iconPosition="start"
                        />
                    </Tabs>

                    {/* Tab content */}
                    <Box sx={{ flex: 1, overflowY: "auto" }}>
                        {tab === 0 && (
                            <SessionSetup
                                session={session}
                                onSessionChange={setSession}
                                isOnline={isOnline}
                                loading={loadingSession}
                            />
                        )}
                        {tab === 1 && session && <SalesFloor session={session} />}
                        {tab === 2 && <OrderHistory session={session} />}
                        {tab === 3 && <SyncPanel />}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default InflightSales;
